import _ from 'lodash';
import { combineReducers, Reducer } from 'redux';

import { LoadActionTypes } from 'uf/data/ActionTypes';
import { DataState, DataStateMap } from 'uf/data/dataState';
import { makeDataStateListReducer } from 'uf/data/makeDataStateListReducer';
import { makeKeyedPromiseReducer } from 'uf/data/makeKeyedPromiseReducer';

interface APIActionTypes {
  [api: string]: { [method: string]: LoadActionTypes };
}

/**
 * HTTP method types allowed by Swagger 2.0
 * See: https://swagger.io/docs/specification/2-0/paths-and-operations/
 *
 * TODO: Move this to the code-gen?
 */
type HttpMethod =
  | 'get'
  | 'post'
  | 'put'
  | 'patch'
  | 'delete'
  | 'head'
  | 'options';

// TODO: Move this interface to the code-gen?
interface APIDetails {
  [tagName: string]: {
    [operationId: string]: {
      httpMethod: HttpMethod;
    };
  };
}

// type ReduxState<API extends APIActionTypes> = {
//   [P in keyof API]: {
//     [Q in keyof API[P]]: DataStateMap<any> | DataState<any>[];
//   };
// };
type DataReducer = Reducer<DataStateMap<any>> | Reducer<DataState<any>[]>;

/**
 * Create a generic API for a swagger reducer
 *
 * actionTypesMap is in the form:
 *
 * ```
 *  {
 *    [tag: string]: {
 *      [operationId: string]: {
 *        LOAD: 'uf-api/...',
 *        SUCCESS: 'uf-api/...' }
 *    }
 *  }
 * ```
 *
 * Note that `tag` is just a swagger tag like `layer`, and `operationId` is just
 * a method name like `get_layer`
 */
export function makeAPIReducer<
  TActionTypes extends APIActionTypes,
  TAPIDetails extends APIDetails,
>(
  actionTypesMap: TActionTypes,
  apiDetailsMap: TAPIDetails,
  // TODO: get this to work - ): Reducer<ReduxState<TActionTypes>> {
): Reducer {
  const reducerMapPairs = Object.entries(actionTypesMap)
    // some auto-generated APIs have no valid methods!
    .filter(
      ([unusedTagName, operationActionTypes]) =>
        !_.isEmpty(operationActionTypes),
    )
    .map(([tagName, operationActionTypes]): [string, Reducer] => {
      const apiReducerPairs = Object.entries(operationActionTypes).map(
        ([operationId, loadActionTypes]): [string, DataReducer] => {
          const { httpMethod } = apiDetailsMap?.[tagName]?.[operationId];
          switch (httpMethod) {
            case 'post':
            case 'put':
            case 'patch':
            case 'delete':
              return [operationId, makeDataStateListReducer(loadActionTypes)];

            case 'get':
            case 'head':
            case 'options':
            default:
              return [operationId, makeKeyedPromiseReducer(loadActionTypes)];
          }
        },
      );
      const reducer = combineReducers(_.fromPairs(apiReducerPairs));
      return [tagName, reducer];
    });
  return combineReducers(_.fromPairs(reducerMapPairs));
}
